import { useEffect, useState, useMemo, useRef} from "react";
import {
  Plus,
  Sigma,
  Filter,
  Layers,
  Save,
  Trash2,
  Loader2,
  X,
  Search
} from "lucide-react";
import { toast } from "react-toastify";

import {
  createSqlExpression,
  getSqlExpressionsBySource,
  getSqlValidationContext,
  updateSqlExpression,
  deleteSqlExpression,
  validateKnowledgeSql,
} from "../../../../apiWithKnowledge";
import SqlValidationEditor from "./SqlValidationEditor.jsx";
import { rewriteSqlForEditor } from "./sqlReferenceTransforms.js";
import { useIngestionJobs } from "../../ingestion/IngestionJobsContext";
import { IngestionQueueStrip, IngestionStatusBadge } from "../../ingestion/IngestionFeedback";

/**
 * SQLExpressionsTab
 * --------------------------------------------------
 * Named SQL query assets for AI:
 * - Measures
 * - Dimensions
 * - Filters
 */
function firstValidationMessage(response) {
  return response?.data?.diagnostics?.[0]?.message || "Please fix the SQL validation issues before saving.";
}

export function formatSynonymsForInput(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ");
  }
  return typeof value === "string" ? value : "";
}

export function normalizeSynonymsForPayload(value) {
  const rawItems = Array.isArray(value) ? value : String(value || "").split(",");

  return rawItems
    .map((item) => (typeof item === "string" ? item.trim().replace(/\s+/g, " ") : ""))
    .filter(Boolean)
    .filter(
      (item, index, items) =>
        items.findIndex((candidate) => candidate.toLowerCase() === item.toLowerCase()) === index
    );
}

export default function SQLExpressionsTab({ sourceDbId }) {
  const createButtonRef = useRef(null);
  const [expressions, setExpressions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(""); // <-- Added state
  const [referenceSchema, setReferenceSchema] = useState({});

  /* --------------------------------------------------
     Memoized Search Filter
  -------------------------------------------------- */
  const filteredExpressions = useMemo(() => {
    if (!searchQuery.trim()) return expressions;
    
    const lower = searchQuery.toLowerCase();
    return expressions.filter((e) => {
      const matchName = (e.name || "").toLowerCase().includes(lower);
      const matchType = (e.expression_type || "").toLowerCase().includes(lower);
      const matchSql = (e.expression_sql || "").toLowerCase().includes(lower);
      
      return matchName || matchType || matchSql;
    });
  }, [expressions, searchQuery]);
  // Form state
  const [editingId, setEditingId] = useState(null);
  const [expressionType, setExpressionType] = useState("Measure");
  const [name, setName] = useState("");
  const [expressionSql, setExpressionSql] = useState("");
  const [formula, setFormula] = useState("");
  const [synonyms, setSynonyms] = useState("");
  const [instructions, setInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [saveValidating, setSaveValidating] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ name: "", expressionSql: "" });
  const { registerJob, getJob, retryJob } = useIngestionJobs({
    sourceDbId,
    resourceTypes: ["sql_expression"],
  });

  /* --------------------------------------------------
     Load expressions
  -------------------------------------------------- */
  useEffect(() => {
    if (!sourceDbId) return;

    setLoading(true);
    Promise.all([
      getSqlValidationContext(sourceDbId).catch(() => ({ data: null })),
      getSqlExpressionsBySource(sourceDbId),
    ])
      .then(([contextRes, expressionRes]) => {
        const schema = contextRes?.data?.schema || {};
        setReferenceSchema(schema);
        setExpressions(
          (expressionRes?.data || []).map((item) => ({
            ...item,
            expression_sql: rewriteSqlForEditor(item.expression_sql, schema),
          })),
        );
      })
      .catch(() => toast.error("Failed to load SQL expressions"))
      .finally(() => setLoading(false));
  }, [sourceDbId]);

  /* --------------------------------------------------
     Reset form
  -------------------------------------------------- */
  const resetForm = () => {
    setEditingId(null);
    setExpressionType("Measure");
    setName("");
    setExpressionSql("");
    setFormula("");
    setSynonyms("");
    setInstructions("");
    setShowCreateForm(false);
    setSaveValidating(false);
    setFieldErrors({ name: "", expressionSql: "" });
  };

  const closeForm = () => {
    resetForm();
    window.setTimeout(() => createButtonRef.current?.focus(), 0);
  };

  /* --------------------------------------------------
     Save expression
  -------------------------------------------------- */
  const handleSave = async () => {
    const nextErrors = {
      name: name.trim() ? "" : "Name is required",
      expressionSql: expressionSql.trim() ? "" : "SQL expression is required",
    };
    setFieldErrors(nextErrors);

    if (nextErrors.name || nextErrors.expressionSql) {
      return;
    }

    setSaveValidating(true);
    try {
      const validationResponse = await validateKnowledgeSql({
        source_db_id: sourceDbId,
        sql: expressionSql,
        mode: "sql_expression",
        expression_type: expressionType,
      });

      if (!validationResponse?.data?.valid) {
        toast.error(firstValidationMessage(validationResponse));
        return;
      }
    } catch {
      toast.error("SQL validation failed. Please try again.");
      return;
    } finally {
      setSaveValidating(false);
    }

    setSaving(true);
    try {
      const payload = {
        source_db_id: sourceDbId,
        expression_type: expressionType,
        name,
        expression_sql: expressionSql,
        formula,
        synonyms: normalizeSynonymsForPayload(synonyms),
        instructions,
      };

      const response = editingId
        ? await updateSqlExpression(editingId, payload)
        : await createSqlExpression(payload);
      const saved = response?.data?.data;
      if (saved) {
        const normalized = {
          ...saved,
          expression_sql: rewriteSqlForEditor(saved.expression_sql, referenceSchema),
        };
        setExpressions((current) => editingId
          ? current.map((item) => String(item.id) === String(saved.id) ? normalized : item)
          : [normalized, ...current]);
        if (response?.data?.ingestion) {
          registerJob({
            summary: response.data.ingestion,
            sourceDbId,
            resourceId: saved.id,
            label: saved.name,
          });
        }
      }
      toast.success(editingId ? "Expression updated" : "Expression created");
      closeForm();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save expression");
    } finally {
      setSaving(false);
    }
  };

  /* --------------------------------------------------
     Edit
  -------------------------------------------------- */
  const handleEdit = (exp) => {
    setEditingId(exp.id);
    setExpressionType(exp.expression_type);
    setName(exp.name);
    setExpressionSql(exp.expression_sql);
    setFormula(exp.formula || "");
    setSynonyms(formatSynonymsForInput(exp.synonyms));
    setInstructions(exp.instructions || "");
    setShowCreateForm(true);
  };

  /* --------------------------------------------------
     Delete
  -------------------------------------------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this SQL expression?")) return;

    try {
      const expression = expressions.find((item) => String(item.id) === String(id));
      const response = await deleteSqlExpression(id);
      setExpressions((prev) => prev.filter((e) => e.id !== id));
      if (response?.data?.ingestion) {
        registerJob({
          summary: response.data.ingestion,
          sourceDbId,
          resourceId: id,
          label: expression?.name || "Expression update",
        });
      }
      toast.success("Expression deleted");
    } catch {
      toast.error("Failed to delete expression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading SQL expressions…
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-6xl">

      {/* ================= STICKY HEADER & SEARCH ================= */}
      <div className="sticky top-[-8px] z-20 bg-background py-3 mb-4 border-b border-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        
        <h3 className="text-sm font-semibold flex items-center gap-2 shrink-0">
          <Sigma className="w-4 h-4" />
          Existing Expressions
          <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full ml-1">
            {filteredExpressions.length}
          </span>
        </h3>

        <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
          {/* Search Bar - Side-by-side */}
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, type, or SQL..."
              className="w-full border border-border rounded-md pl-9 pr-8 py-1.5 text-sm bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Create Button */}
          <button
            ref={createButtonRef}
            onClick={() => {
              resetForm();
              setShowCreateForm(true);
            }}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 transition-colors shrink-0 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
        </div>
      </div>

      {/* ================= MODAL ================= */}
      {showCreateForm && (
        <div className=" fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
          <div className=" no-scrollbar bg-background rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border backdrop-blur-sm">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border border-border">
              <h2 className="text-xl font-semibold">
                {editingId ? "Edit SQL Expression" : "Create SQL Expression"}
              </h2>
              <button
                onClick={closeForm}
                className="p-1 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className=" no-scrollbar px-6 py-6 overflow-y-auto flex-1 min-h-0">
              <div className="space-y-5">
                {/* Type */}
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">Expression type</p>
                    <p className="text-xs text-muted-foreground">
                      This is used for categorization only. The SQL below should be a fragment, not a full query.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <TypeButton
                      active={expressionType === "Measure"}
                      icon={Sigma}
                      label="Measure"
                      onClick={() => setExpressionType("Measure")}
                    />
                    <TypeButton
                      active={expressionType === "Dimension"}
                      icon={Layers}
                      label="Dimension"
                      onClick={() => setExpressionType("Dimension")}
                    />
                    <TypeButton
                      active={expressionType === "Filter"}
                      icon={Filter}
                      label="Filter"
                      onClick={() => setExpressionType("Filter")}
                    />
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Name</p>
                  <input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (fieldErrors.name) {
                        setFieldErrors((prev) => ({ ...prev, name: "" }));
                      }
                    }}
                    placeholder="Expression name (e.g. Total Collection)"
                    className={`w-full rounded-md px-3 py-2 text-sm bg-background text-foreground ${
                      fieldErrors.name
                        ? "border border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                        : "border border-border"
                    }`}
                  />
                  {fieldErrors.name && (
                    <p className="text-xs text-red-600 dark:text-red-400">{fieldErrors.name}</p>
                  )}
                </div>

                {/* SQL */}
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">SQL expression</p>
                    <p className="text-xs text-muted-foreground">
                      Enter a SQL expression fragment. Do not include `SELECT` or `FROM`.
                    </p>
                  </div>
                  <SqlValidationEditor
                    sourceDbId={sourceDbId}
                    mode="sql_expression"
                    expressionType={expressionType}
                    value={expressionSql}
                    onValueChange={(nextValue) => {
                      setExpressionSql(nextValue);
                      if (fieldErrors.expressionSql) {
                        setFieldErrors((prev) => ({ ...prev, expressionSql: "" }));
                      }
                    }}
                    rows={5}
                    placeholder="SUM(CASE WHEN X_Bucket.Current_DPD >= 90 THEN X_Bucket.Outstanding_Amount ELSE 0 END) * 1.0 / NULLIF(SUM(X_Bucket.Outstanding_Amount), 0)"
                    minHeightClassName="min-h-[180px]"
                    requiredMessage={fieldErrors.expressionSql}
                  />
                </div>

                {/* Formula */}
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">Formula</p>
                    <p className="text-xs text-muted-foreground">
                      Optional plain-language formula or business definition for this query.
                    </p>
                  </div>
                  <input
                    value={formula}
                    onChange={(e) => setFormula(e.target.value)}
                    placeholder="Optional business formula or explanation"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  />
                </div>

                {/* Synonyms */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Synonyms</p>
                  <input
                    value={synonyms}
                    onChange={(e) => setSynonyms(e.target.value)}
                    placeholder="Synonyms (comma separated)"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  />
                </div>

                {/* Instructions */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">Instructions</p>
                  <textarea
                    rows={2}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Optional AI usage instructions"
                    className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-border bg-muted/30">
              <div className="flex justify-end gap-2">
                <button
                  onClick={closeForm}
                  className="px-4 py-2 text-sm border border-border rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || saveValidating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm"
                >
                  {saving || saveValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {editingId ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <IngestionQueueStrip sourceDbId={sourceDbId} resourceTypes={["sql_expression"]} />

      {/* ================= LIST ================= */}
      <div className="space-y-3">

        {expressions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No SQL expressions defined yet.
          </p>
        )}

        {filteredExpressions.map((exp) => (
          <div
            key={exp.id}
            className="border border-border rounded-lg p-4 flex justify-between items-start"
          >
            <div className="text-sm space-y-1">
              <div className="font-medium flex flex-wrap items-center gap-2">
                {exp.name}
                <span className="ml-2 text-xs text-muted-foreground">
                  ({exp.expression_type})
                </span>
                <IngestionStatusBadge
                  compact
                  job={getJob("sql_expression", exp.id)}
                  onRetry={() => retryJob(getJob("sql_expression", exp.id)?.jobId)}
                  onReview={() => handleEdit(exp)}
                />
              </div>
              <code className="text-xs text-muted-foreground block">
                {exp.expression_sql}
              </code>
            </div>

            <div className="flex gap-1">
              <button
                onClick={() => handleEdit(exp)}
                className="px-2 py-1 text-xs border border-border rounded"
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(exp.id)}
                className="text-destructive hover:bg-destructive/10 rounded p-2"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------
   Expression Type Button
-------------------------------------------------- */
function TypeButton({ active, icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium
        ${active
          ? "bg-background border shadow"
          : "text-muted-foreground hover:bg-background/50"}
      `}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
